<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import AddPhoneCentral from './AddPhoneCentral.vue';
import EditPhoneCentral from './EditPhoneCentral.vue';
import CallLogs from './CallLogs.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const activeTab = ref('configs');
const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedConfig = ref({});
const copiedId = ref(null);

const records = computed(
  () => getters['phoneCentralConfigs/getConfigs'].value
);
const uiFlags = computed(
  () => getters['phoneCentralConfigs/getUIFlags'].value
);

const tableHeaders = computed(() => [
  t('PHONE_CENTRAL.LIST.TABLE_HEADER.INBOX'),
  t('PHONE_CENTRAL.LIST.TABLE_HEADER.MESSAGE'),
  t('PHONE_CENTRAL.LIST.TABLE_HEADER.PRIVATE'),
  t('PHONE_CENTRAL.LIST.TABLE_HEADER.LABEL'),
  t('PHONE_CENTRAL.LIST.TABLE_HEADER.WEBHOOK_URL'),
  t('PHONE_CENTRAL.LIST.TABLE_HEADER.ACTIONS'),
]);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = config => {
  showEditPopup.value = true;
  selectedConfig.value = config;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = config => {
  showDeleteConfirmationPopup.value = true;
  selectedConfig.value = config;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteConfig = async id => {
  try {
    await store.dispatch('phoneCentralConfigs/delete', id);
    useAlert(t('PHONE_CENTRAL.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('PHONE_CENTRAL.DELETE.API.ERROR_MESSAGE')
    );
  } finally {
    loading.value[selectedConfig.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedConfig.value.id] = true;
  closeDeletePopup();
  deleteConfig(selectedConfig.value.id);
};

const copyWebhookUrl = config => {
  navigator.clipboard.writeText(config.webhook_url);
  copiedId.value = config.id;
  useAlert(t('PHONE_CENTRAL.COPY.SUCCESS'));
  setTimeout(() => {
    copiedId.value = null;
  }, 2000);
};

onBeforeMount(() => {
  store.dispatch('phoneCentralConfigs/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="activeTab === 'configs' && uiFlags.isFetching"
    :loading-message="$t('PHONE_CENTRAL.LOADING')"
    :no-records-found="activeTab === 'configs' && !records.length"
    :no-records-message="$t('PHONE_CENTRAL.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PHONE_CENTRAL.HEADER')"
        :description="$t('PHONE_CENTRAL.DESCRIPTION')"
      >
        <template v-if="activeTab === 'configs' && records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ records.length }}
            {{ records.length === 1 ? 'configuração' : 'configurações' }}
          </span>
        </template>
        <template #actions>
          <Button
            v-if="activeTab === 'configs'"
            :label="$t('PHONE_CENTRAL.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <!-- Tabs -->
      <div class="flex border-b border-n-weak mb-4">
        <button
          class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
          :class="
            activeTab === 'configs'
              ? 'border-n-brand-9 text-n-brand-11'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'configs'"
        >
          {{ $t('PHONE_CENTRAL.TABS.CONFIGS') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
          :class="
            activeTab === 'call_logs'
              ? 'border-n-brand-9 text-n-brand-11'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'call_logs'"
        >
          {{ $t('PHONE_CENTRAL.TABS.CALL_LOGS') }}
        </button>
      </div>
    </template>

    <template #body>
      <!-- Configs Tab -->
      <template v-if="activeTab === 'configs'">
        <BaseTable
          :headers="tableHeaders"
          :items="records"
          :no-data-message="$t('PHONE_CENTRAL.LIST.404')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="config in items"
              :key="config.id"
              :item="config"
            >
              <template #default>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12">
                    {{ config.inbox_name || `Inbox #${config.inbox_id}` }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 truncate max-w-[200px] block"
                  >
                    {{ config.message_template || '-' }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ config.private_note ? 'Sim' : 'Não' }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    v-if="config.label_name"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-n-brand-2 text-n-brand-11"
                  >
                    {{ config.label_name }}
                  </span>
                  <span v-else class="text-n-slate-11">-</span>
                </BaseTableCell>

                <BaseTableCell>
                  <div class="flex items-center gap-2 max-w-[300px]">
                    <span
                      class="text-body-main text-n-slate-11 truncate text-xs"
                    >
                      {{ config.webhook_url }}
                    </span>
                    <Button
                      v-tooltip.top="$t('PHONE_CENTRAL.COPY.BUTTON')"
                      :icon="
                        copiedId === config.id
                          ? 'i-lucide-check'
                          : 'i-lucide-copy'
                      "
                      slate
                      xs
                      @click="copyWebhookUrl(config)"
                    />
                  </div>
                </BaseTableCell>

                <BaseTableCell align="end">
                  <div class="flex gap-3 justify-end flex-shrink-0">
                    <Button
                      v-tooltip.top="$t('PHONE_CENTRAL.FORM.EDIT')"
                      icon="i-woot-edit-pen"
                      slate
                      sm
                      :is-loading="loading[config.id]"
                      @click="openEditPopup(config)"
                    />
                    <Button
                      v-tooltip.top="$t('PHONE_CENTRAL.FORM.DELETE')"
                      icon="i-woot-bin"
                      slate
                      sm
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      :is-loading="loading[config.id]"
                      @click="openDeletePopup(config)"
                    />
                  </div>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </template>

      <!-- Call Logs Tab -->
      <CallLogs v-if="activeTab === 'call_logs'" />
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddPhoneCentral @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditPhoneCentral
        :selected-config="selectedConfig"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('PHONE_CENTRAL.DELETE.CONFIRM.TITLE')"
      :message="$t('PHONE_CENTRAL.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('PHONE_CENTRAL.DELETE.CONFIRM.YES')"
      :reject-text="$t('PHONE_CENTRAL.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
